<template>
    <div class="modal-overlay" @click.self="$emit('close')">
      <div class="gps-detail-modal">
        <!-- Header -->
        <div class="modal-header">
          <div class="header-left">
            <div class="player-badge">
              <span class="jersey-number" v-if="player.jersey_number">#{{ player.jersey_number }}</span>
              <span class="player-initials" v-else>{{ playerInitials }}</span>
            </div>
            <div class="header-info">
              <h2 class="player-name">{{ playerFullName }}</h2>
              <div class="session-info">
                <span class="session-name">{{ sessionName }}</span>
                <span class="session-date">{{ formattedDate }}</span>
              </div>
            </div>
          </div>
          <div class="header-actions">
            <button
              class="btn btn-secondary btn-sm"
              :class="{ active: comparisonMode }"
              @click="toggleComparisonMode"
            >
              <i class="bi bi-bar-chart-line"></i>
              {{ comparisonMode ? 'Exit Compare' : 'Compare' }}
            </button>
            <button class="close-btn" @click="$emit('close')">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
        </div>

        <!-- Comparison Player Selector -->
        <div v-if="comparisonMode" class="comparison-selector">
          <label>Compare with:</label>
          <div class="player-chips">
            <div
              v-for="p in availablePlayers.filter(ap => ap.athlete_id !== player.athlete_id)"
              :key="p.athlete_id"
              class="player-chip"
              :class="{ selected: selectedComparisonPlayers.includes(p.athlete_id) }"
              @click="toggleComparisonPlayer(p.athlete_id)"
            >
              <span class="chip-jersey" v-if="p.jersey_number">#{{ p.jersey_number }}</span>
              {{ p.first_name }} {{ p.last_name }}
              <i v-if="selectedComparisonPlayers.includes(p.athlete_id)" class="bi bi-check"></i>
            </div>
          </div>
          <p class="comparison-hint" v-if="selectedComparisonPlayers.length === 0">
            Select up to 3 players to compare
          </p>
        </div>

        <!-- Tabs Navigation -->
        <div class="tabs-nav">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            class="tab-btn"
            :class="{ active: activeTab === tab.id }"
            @click="activeTab = tab.id"
          >
            <i :class="'bi bi-' + tab.icon"></i>
            <span>{{ tab.label }}</span>
          </button>
        </div>

        <!-- Tab Content -->
        <div class="modal-body">
          <!-- Summary Tab -->
          <div v-if="activeTab === 'summary'" class="tab-content">
            <div v-if="!comparisonMode || selectedComparisonPlayers.length === 0">
              <!-- Single Player View -->
              <div class="stats-section">
                <h4 class="section-title">Session Statistics</h4>
                <div class="stats-grid">
                  <div class="stat-card">
                    <div class="stat-icon bg-primary">
                      <i class="bi bi-geo-alt"></i>
                    </div>
                    <div class="stat-content">
                      <span class="stat-value">{{ formatDistance(player.total_distance) }}</span>
                      <span class="stat-label">Total Distance</span>
                    </div>
                  </div>
                  <div class="stat-card">
                    <div class="stat-icon bg-warning">
                      <i class="bi bi-lightning-charge"></i>
                    </div>
                    <div class="stat-content">
                      <span class="stat-value">{{ formatNumber(player.total_player_load) }}</span>
                      <span class="stat-label">Player Load</span>
                    </div>
                  </div>
                  <div class="stat-card">
                    <div class="stat-icon bg-info">
                      <i class="bi bi-arrow-up-right"></i>
                    </div>
                    <div class="stat-content">
                      <span class="stat-value">{{ formatDistance(player.sprint_distance || player.velocity_band4_distance) }}</span>
                      <span class="stat-label">Sprint Distance</span>
                    </div>
                  </div>
                  <div class="stat-card">
                    <div class="stat-icon bg-danger">
                      <i class="bi bi-speedometer2"></i>
                    </div>
                    <div class="stat-content">
                      <span class="stat-value">{{ formatSpeed(player.max_velocity) }}</span>
                      <span class="stat-label">Max Speed</span>
                    </div>
                  </div>
                  <div class="stat-card">
                    <div class="stat-icon bg-success">
                      <i class="bi bi-arrow-up"></i>
                    </div>
                    <div class="stat-content">
                      <span class="stat-value">{{ player.high_acceleration_count || 0 }}</span>
                      <span class="stat-label">Accelerations</span>
                    </div>
                  </div>
                  <div class="stat-card">
                    <div class="stat-icon bg-secondary">
                      <i class="bi bi-arrow-down"></i>
                    </div>
                    <div class="stat-content">
                      <span class="stat-value">{{ player.high_deceleration_count || 0 }}</span>
                      <span class="stat-label">Decelerations</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Period Breakdown -->
              <div class="stats-section" v-if="hasPeriodData">
                <h4 class="section-title">Period Breakdown</h4>
                <div class="period-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Period</th>
                        <th>Distance</th>
                        <th>Player Load</th>
                        <th>Sprints</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="period in periodData" :key="period.name">
                        <td class="period-name">{{ period.name }}</td>
                        <td>{{ formatDistance(period.distance) }}</td>
                        <td>{{ formatNumber(period.player_load) }}</td>
                        <td>{{ period.sprints || 0 }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Comparison View -->
            <div v-else class="comparison-view">
              <h4 class="section-title">Player Comparison</h4>
              <div class="comparison-chart-container">
                <canvas ref="comparisonChart"></canvas>
              </div>
              <div class="comparison-table">
                <table>
                  <thead>
                    <tr>
                      <th>Metric</th>
                      <th>{{ playerFullName }}</th>
                      <th v-for="cp in comparisonPlayersData" :key="cp.athlete_id">
                        {{ cp.first_name }} {{ cp.last_name }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Distance</td>
                      <td>{{ formatDistance(player.total_distance) }}</td>
                      <td v-for="cp in comparisonPlayersData" :key="'dist-' + cp.athlete_id">
                        {{ formatDistance(cp.total_distance) }}
                      </td>
                    </tr>
                    <tr>
                      <td>Player Load</td>
                      <td>{{ formatNumber(player.total_player_load) }}</td>
                      <td v-for="cp in comparisonPlayersData" :key="'load-' + cp.athlete_id">
                        {{ formatNumber(cp.total_player_load) }}
                      </td>
                    </tr>
                    <tr>
                      <td>Sprint Distance</td>
                      <td>{{ formatDistance(player.sprint_distance || player.velocity_band4_distance) }}</td>
                      <td v-for="cp in comparisonPlayersData" :key="'sprint-' + cp.athlete_id">
                        {{ formatDistance(cp.sprint_distance || cp.velocity_band4_distance) }}
                      </td>
                    </tr>
                    <tr>
                      <td>Max Speed</td>
                      <td>{{ formatSpeed(player.max_velocity) }}</td>
                      <td v-for="cp in comparisonPlayersData" :key="'speed-' + cp.athlete_id">
                        {{ formatSpeed(cp.max_velocity) }}
                      </td>
                    </tr>
                    <tr>
                      <td>Accelerations</td>
                      <td>{{ player.high_acceleration_count || 0 }}</td>
                      <td v-for="cp in comparisonPlayersData" :key="'accel-' + cp.athlete_id">
                        {{ cp.high_acceleration_count || 0 }}
                      </td>
                    </tr>
                    <tr>
                      <td>Decelerations</td>
                      <td>{{ player.high_deceleration_count || 0 }}</td>
                      <td v-for="cp in comparisonPlayersData" :key="'decel-' + cp.athlete_id">
                        {{ cp.high_deceleration_count || 0 }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Speed Zones Tab -->
          <div v-if="activeTab === 'speed'" class="tab-content">
            <div class="stats-section">
              <h4 class="section-title">Velocity Band Distribution</h4>
              <div class="speed-zones-container">
                <div class="speed-chart-wrapper">
                  <canvas ref="speedZonesChart"></canvas>
                </div>
                <div class="speed-zones-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Zone</th>
                        <th>Speed Range</th>
                        <th>Distance</th>
                        <th>%</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="zone in speedZones" :key="zone.id">
                        <td>
                          <span class="zone-badge" :style="{ background: zone.color }">{{ zone.name }}</span>
                        </td>
                        <td class="zone-range">{{ zone.range }}</td>
                        <td>{{ formatDistance(zone.distance) }}</td>
                        <td class="zone-percentage">{{ zone.percentage }}%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <!-- Acceleration Tab -->
          <div v-if="activeTab === 'acceleration'" class="tab-content">
            <div class="stats-section">
              <h4 class="section-title">Acceleration Efforts</h4>
              <div class="accel-decel-grid">
                <div class="accel-section">
                  <h5><i class="bi bi-arrow-up-circle"></i> Accelerations</h5>
                  <div class="band-list">
                    <div class="band-item" v-for="band in accelerationBands" :key="'accel-' + band.id">
                      <div class="band-header">
                        <span class="band-name">{{ band.name }}</span>
                        <span class="band-range">{{ band.range }}</span>
                      </div>
                      <div class="band-bar">
                        <div class="band-fill" :style="{ width: band.percentage + '%', background: band.color }"></div>
                      </div>
                      <span class="band-count">{{ band.count }} efforts</span>
                    </div>
                  </div>
                  <div class="peak-value">
                    <span class="peak-label">Peak Acceleration</span>
                    <span class="peak-number">{{ formatAccel(player.max_acceleration) }} m/s<sup>2</sup></span>
                  </div>
                </div>
                <div class="decel-section">
                  <h5><i class="bi bi-arrow-down-circle"></i> Decelerations</h5>
                  <div class="band-list">
                    <div class="band-item" v-for="band in decelerationBands" :key="'decel-' + band.id">
                      <div class="band-header">
                        <span class="band-name">{{ band.name }}</span>
                        <span class="band-range">{{ band.range }}</span>
                      </div>
                      <div class="band-bar">
                        <div class="band-fill" :style="{ width: band.percentage + '%', background: band.color }"></div>
                      </div>
                      <span class="band-count">{{ band.count }} efforts</span>
                    </div>
                  </div>
                  <div class="peak-value">
                    <span class="peak-label">Peak Deceleration</span>
                    <span class="peak-number">{{ formatAccel(player.max_deceleration) }} m/s<sup>2</sup></span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- History Tab -->
          <div v-if="activeTab === 'history'" class="tab-content">
            <div class="stats-section">
              <h4 class="section-title">Performance Trend (Last 10 Sessions)</h4>
              <div v-if="!playerHistory || playerHistory.length === 0" class="empty-state">
                <i class="bi bi-graph-up"></i>
                <p>No historical data available</p>
                <button class="btn btn-primary btn-sm" @click="$emit('load-history', player.athlete_id)">
                  Load History
                </button>
              </div>
              <div v-else class="history-chart-container">
                <div class="metric-selector">
                  <label>Metric:</label>
                  <select v-model="historyMetric" @change="updateHistoryChart">
                    <option value="total_distance">Distance</option>
                    <option value="total_player_load">Player Load</option>
                    <option value="sprint_distance">Sprint Distance</option>
                    <option value="max_velocity">Max Speed</option>
                  </select>
                </div>
                <div class="history-chart-wrapper">
                  <canvas ref="historyChart"></canvas>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
</template>

<script>
import { Chart, registerables } from 'chart.js'

Chart.register(...registerables)

export default {
  name: 'PlayerGpsDetailModal',

  props: {
    player: {
      type: Object,
      required: true
    },
    sessionName: {
      type: String,
      default: ''
    },
    sessionDate: {
      type: String,
      default: ''
    },
    availablePlayers: {
      type: Array,
      default: () => []
    },
    playerHistory: {
      type: Array,
      default: () => []
    }
  },

  emits: ['close', 'load-history', 'load-comparison'],

  data() {
    return {
      activeTab: 'summary',
      comparisonMode: false,
      selectedComparisonPlayers: [],
      comparisonPlayersData: [],
      historyMetric: 'total_distance',
      tabs: [
        { id: 'summary', label: 'Summary', icon: 'speedometer2' },
        { id: 'speed', label: 'Speed Zones', icon: 'graph-up' },
        { id: 'acceleration', label: 'Acceleration', icon: 'lightning' },
        { id: 'history', label: 'History', icon: 'clock-history' }
      ],
      charts: {
        speedZones: null,
        comparison: null,
        history: null
      }
    }
  },

  computed: {
    playerFullName() {
      return `${this.player.first_name || ''} ${this.player.last_name || ''}`.trim() || 'Unknown Player'
    },

    playerInitials() {
      const first = (this.player.first_name || '')[0] || ''
      const last = (this.player.last_name || '')[0] || ''
      return (first + last).toUpperCase() || '?'
    },

    formattedDate() {
      if (!this.sessionDate) return ''
      return new Date(this.sessionDate).toLocaleDateString('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    },

    hasPeriodData() {
      return this.player.periods && this.player.periods.length > 0
    },

    periodData() {
      if (!this.player.periods) {
        // Default periods if not provided
        return [
          { name: '1st Half', distance: this.player.first_half_distance, player_load: this.player.first_half_load, sprints: this.player.first_half_sprints },
          { name: '2nd Half', distance: this.player.second_half_distance, player_load: this.player.second_half_load, sprints: this.player.second_half_sprints }
        ].filter(p => p.distance || p.player_load)
      }
      return this.player.periods
    },

    speedZones() {
      const zones = [
        { id: 1, name: 'Zone 1', range: '0-6 km/h', color: '#6c757d', key: 'velocity_band1_distance' },
        { id: 2, name: 'Zone 2', range: '6-12 km/h', color: '#28a745', key: 'velocity_band2_distance' },
        { id: 3, name: 'Zone 3', range: '12-18 km/h', color: '#ffc107', key: 'velocity_band3_distance' },
        { id: 4, name: 'Zone 4', range: '18-21 km/h', color: '#fd7e14', key: 'velocity_band4_distance' },
        { id: 5, name: 'Zone 5', range: '21-24 km/h', color: '#dc3545', key: 'velocity_band5_distance' },
        { id: 6, name: 'Zone 6', range: '24+ km/h', color: '#6f42c1', key: 'velocity_band6_distance' }
      ]

      const total = zones.reduce((sum, z) => sum + (this.player[z.key] || 0), 0)

      return zones.map(zone => ({
        ...zone,
        distance: this.player[zone.key] || 0,
        percentage: total > 0 ? Math.round((this.player[zone.key] || 0) / total * 100) : 0
      }))
    },

    accelerationBands() {
      const totalAccel = (this.player.accel_band1_count || 0) +
                         (this.player.accel_band2_count || 0) +
                         (this.player.accel_band3_count || 0)
      return [
        {
          id: 1,
          name: 'Band 1',
          range: '2-3 m/s²',
          color: '#28a745',
          count: this.player.accel_band1_count || 0,
          percentage: totalAccel > 0 ? Math.round((this.player.accel_band1_count || 0) / totalAccel * 100) : 0
        },
        {
          id: 2,
          name: 'Band 2',
          range: '3-4 m/s²',
          color: '#ffc107',
          count: this.player.accel_band2_count || 0,
          percentage: totalAccel > 0 ? Math.round((this.player.accel_band2_count || 0) / totalAccel * 100) : 0
        },
        {
          id: 3,
          name: 'Band 3',
          range: '4+ m/s²',
          color: '#dc3545',
          count: this.player.accel_band3_count || 0,
          percentage: totalAccel > 0 ? Math.round((this.player.accel_band3_count || 0) / totalAccel * 100) : 0
        }
      ]
    },

    decelerationBands() {
      const totalDecel = (this.player.decel_band1_count || 0) +
                         (this.player.decel_band2_count || 0) +
                         (this.player.decel_band3_count || 0)
      return [
        {
          id: 1,
          name: 'Band 1',
          range: '-2 to -3 m/s²',
          color: '#17a2b8',
          count: this.player.decel_band1_count || 0,
          percentage: totalDecel > 0 ? Math.round((this.player.decel_band1_count || 0) / totalDecel * 100) : 0
        },
        {
          id: 2,
          name: 'Band 2',
          range: '-3 to -4 m/s²',
          color: '#6f42c1',
          count: this.player.decel_band2_count || 0,
          percentage: totalDecel > 0 ? Math.round((this.player.decel_band2_count || 0) / totalDecel * 100) : 0
        },
        {
          id: 3,
          name: 'Band 3',
          range: '-4+ m/s²',
          color: '#e83e8c',
          count: this.player.decel_band3_count || 0,
          percentage: totalDecel > 0 ? Math.round((this.player.decel_band3_count || 0) / totalDecel * 100) : 0
        }
      ]
    }
  },

  watch: {
    activeTab(newTab) {
      this.$nextTick(() => {
        if (newTab === 'speed') {
          this.initSpeedZonesChart()
        } else if (newTab === 'history' && this.playerHistory && this.playerHistory.length > 0) {
          this.initHistoryChart()
        }
      })
    },

    comparisonMode(enabled) {
      if (!enabled) {
        this.selectedComparisonPlayers = []
        this.comparisonPlayersData = []
        this.destroyChart('comparison')
      }
    },

    selectedComparisonPlayers: {
      handler(newVal) {
        if (newVal.length > 0) {
          this.$emit('load-comparison', newVal)
        }
      },
      deep: true
    },

    playerHistory: {
      handler(newVal) {
        if (newVal && newVal.length > 0 && this.activeTab === 'history') {
          this.$nextTick(() => {
            this.initHistoryChart()
          })
        }
      },
      deep: true
    }
  },

  mounted() {
    if (this.activeTab === 'speed') {
      this.$nextTick(() => {
        this.initSpeedZonesChart()
      })
    }
  },

  beforeUnmount() {
    Object.keys(this.charts).forEach(key => {
      this.destroyChart(key)
    })
  },

  methods: {
    toggleComparisonMode() {
      this.comparisonMode = !this.comparisonMode
    },

    toggleComparisonPlayer(athleteId) {
      const index = this.selectedComparisonPlayers.indexOf(athleteId)
      if (index > -1) {
        this.selectedComparisonPlayers.splice(index, 1)
      } else if (this.selectedComparisonPlayers.length < 3) {
        this.selectedComparisonPlayers.push(athleteId)
      }
    },

    setComparisonData(playersData) {
      this.comparisonPlayersData = playersData
      this.$nextTick(() => {
        this.initComparisonChart()
      })
    },

    formatDistance(meters) {
      if (!meters && meters !== 0) return '-'
      if (meters >= 1000) {
        return (meters / 1000).toFixed(2) + ' km'
      }
      return Math.round(meters) + ' m'
    },

    formatSpeed(mps) {
      if (!mps && mps !== 0) return '-'
      const kmh = mps * 3.6
      return kmh.toFixed(1) + ' km/h'
    },

    formatNumber(num) {
      if (!num && num !== 0) return '-'
      return Number(num).toFixed(1)
    },

    formatAccel(value) {
      if (!value && value !== 0) return '-'
      return Math.abs(Number(value)).toFixed(2)
    },

    destroyChart(chartKey) {
      if (this.charts[chartKey]) {
        this.charts[chartKey].destroy()
        this.charts[chartKey] = null
      }
    },

    initSpeedZonesChart() {
      this.destroyChart('speedZones')
      const canvas = this.$refs.speedZonesChart
      if (!canvas) return

      const ctx = canvas.getContext('2d')
      this.charts.speedZones = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: this.speedZones.map(z => z.name),
          datasets: [{
            data: this.speedZones.map(z => z.distance),
            backgroundColor: this.speedZones.map(z => z.color),
            borderWidth: 2,
            borderColor: '#1a1d29'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '60%',
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const zone = this.speedZones[context.dataIndex]
                  return `${zone.name}: ${this.formatDistance(zone.distance)} (${zone.percentage}%)`
                }
              }
            }
          }
        }
      })
    },

    initComparisonChart() {
      this.destroyChart('comparison')
      const canvas = this.$refs.comparisonChart
      if (!canvas) return

      const ctx = canvas.getContext('2d')
      const labels = ['Distance (km)', 'Player Load', 'Sprint Dist (m)', 'Max Speed', 'Accels', 'Decels']

      const allPlayers = [this.player, ...this.comparisonPlayersData]
      const colors = ['#667eea', '#28a745', '#ffc107', '#dc3545']

      const datasets = allPlayers.map((p, index) => ({
        label: `${p.first_name} ${p.last_name}`,
        data: [
          (p.total_distance || 0) / 1000,
          p.total_player_load || 0,
          p.sprint_distance || p.velocity_band4_distance || 0,
          (p.max_velocity || 0) * 3.6,
          p.high_acceleration_count || 0,
          p.high_deceleration_count || 0
        ],
        backgroundColor: colors[index] + '99',
        borderColor: colors[index],
        borderWidth: 1
      }))

      this.charts.comparison = new Chart(ctx, {
        type: 'bar',
        data: { labels, datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'top',
              labels: {
                color: '#adb5bd'
              }
            }
          },
          scales: {
            x: {
              grid: { color: '#404258' },
              ticks: { color: '#adb5bd' }
            },
            y: {
              grid: { color: '#404258' },
              ticks: { color: '#adb5bd' }
            }
          }
        }
      })
    },

    initHistoryChart() {
      this.destroyChart('history')
      const canvas = this.$refs.historyChart
      if (!canvas) return

      const ctx = canvas.getContext('2d')
      const metric = this.historyMetric

      const labels = this.playerHistory.map(h => {
        return new Date(h.session_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
      })

      const data = this.playerHistory.map(h => {
        let value = h[metric] || 0
        if (metric === 'total_distance' || metric === 'sprint_distance') {
          value = value / 1000 // Convert to km
        } else if (metric === 'max_velocity') {
          value = value * 3.6 // Convert to km/h
        }
        return value
      })

      this.charts.history = new Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label: this.getMetricLabel(metric),
            data,
            borderColor: '#667eea',
            backgroundColor: 'rgba(102, 126, 234, 0.2)',
            fill: true,
            tension: 0.3,
            pointBackgroundColor: '#667eea',
            pointBorderColor: '#fff',
            pointRadius: 5,
            pointHoverRadius: 7
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  return `${this.getMetricLabel(metric)}: ${context.parsed.y.toFixed(2)}`
                }
              }
            }
          },
          scales: {
            x: {
              grid: { color: '#404258' },
              ticks: { color: '#adb5bd' }
            },
            y: {
              grid: { color: '#404258' },
              ticks: { color: '#adb5bd' }
            }
          }
        }
      })
    },

    updateHistoryChart() {
      this.initHistoryChart()
    },

    getMetricLabel(metric) {
      const labels = {
        total_distance: 'Distance (km)',
        total_player_load: 'Player Load',
        sprint_distance: 'Sprint Distance (km)',
        max_velocity: 'Max Speed (km/h)'
      }
      return labels[metric] || metric
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  z-index: 1050;
  backdrop-filter: blur(4px);
}

.gps-detail-modal {
  background: var(--color-surface);
  border: none;
  box-shadow: var(--shadow-lg);
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header Styles */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-lg);
  border-bottom: 1px solid var(--color-border);
  background: var(--color-surface-alt);
}

.header-left {
  display: flex;
  align-items: center;
  gap: var(--space-md);
}

.player-badge {
  width: 56px;
  height: 56px;
  border-radius: var(--radius-lg);
  background: var(--color-primary-gradient);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 1.25rem;
}

.jersey-number {
  font-size: 1.1rem;
}

.player-initials {
  font-size: 1.25rem;
}

.header-info {
  display: flex;
  flex-direction: column;
}

.player-name {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.session-info {
  display: flex;
  gap: var(--space-sm);
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  margin-top: 4px;
}

.session-name {
  font-weight: 500;
}

.session-date::before {
  content: '|';
  margin-right: var(--space-sm);
  opacity: 0.5;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}

.header-actions .btn.active {
  background: var(--color-primary);
  color: white;
  border-color: var(--color-primary);
}

.close-btn {
  background: transparent;
  border: none;
  color: var(--color-text-secondary);
  font-size: 1.25rem;
  cursor: pointer;
  padding: var(--space-sm);
  border-radius: var(--radius-md);
  transition: all var(--transition-fast);
}

.close-btn:hover {
  background: var(--color-surface-raised);
  color: var(--color-text);
}

/* Comparison Selector */
.comparison-selector {
  padding: var(--space-md) var(--space-lg);
  background: var(--color-surface-alt);
  border-bottom: 1px solid var(--color-border);
}

.comparison-selector label {
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  margin-bottom: var(--space-sm);
  display: block;
}

.player-chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-sm);
}

.player-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: var(--color-surface-raised);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-full);
  font-size: 0.8125rem;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.player-chip:hover {
  border-color: var(--color-primary);
}

.player-chip.selected {
  background: var(--color-primary-bg);
  border-color: var(--color-primary);
  color: var(--color-primary-light);
}

.chip-jersey {
  font-weight: 600;
}

.comparison-hint {
  font-size: 0.75rem;
  color: var(--color-text-muted);
  margin-top: var(--space-sm);
  margin-bottom: 0;
}

/* Tabs Navigation */
.tabs-nav {
  display: flex;
  gap: 2px;
  padding: var(--space-sm) var(--space-lg);
  background: var(--color-surface-alt);
  border-bottom: 1px solid var(--color-border);
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-sm) var(--space-md);
  background: transparent;
  border: none;
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.tab-btn:hover {
  background: var(--color-surface-raised);
  color: var(--color-text);
}

.tab-btn.active {
  background: var(--color-primary);
  color: white;
}

.tab-btn i {
  font-size: 1rem;
}

/* Modal Body */
.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: var(--space-xl);
}

.tab-content {
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Stats Section */
.stats-section {
  margin-bottom: var(--space-xl);
}

.stats-section:last-child {
  margin-bottom: 0;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 var(--space-md) 0;
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: var(--space-md);
}

.stat-card {
  background: var(--color-surface-alt);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-lg);
  padding: var(--space-md);
  display: flex;
  align-items: center;
  gap: var(--space-md);
}

.stat-icon {
  width: 44px;
  height: 44px;
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
  color: white;
  flex-shrink: 0;
}

.stat-icon.bg-primary { background: var(--color-primary); }
.stat-icon.bg-warning { background: var(--color-warning); color: #333; }
.stat-icon.bg-info { background: var(--color-info); }
.stat-icon.bg-danger { background: var(--color-danger); }
.stat-icon.bg-success { background: var(--color-success); }
.stat-icon.bg-secondary { background: #6c757d; }

.stat-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.stat-value {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--color-text);
}

.stat-label {
  font-size: 0.75rem;
  color: var(--color-text-muted);
  white-space: nowrap;
}

/* Period Table */
.period-table {
  background: var(--color-surface-alt);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-lg);
  overflow: hidden;
}

.period-table table {
  width: 100%;
  border-collapse: collapse;
}

.period-table th,
.period-table td {
  padding: var(--space-sm) var(--space-md);
  text-align: left;
}

.period-table th {
  background: var(--color-surface-raised);
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-text-muted);
  text-transform: uppercase;
}

.period-table tr:not(:last-child) td {
  border-bottom: 1px solid var(--color-border-light);
}

.period-name {
  font-weight: 500;
}

/* Comparison View */
.comparison-view {
  margin-top: var(--space-md);
}

.comparison-chart-container {
  height: 350px;
  margin-bottom: var(--space-lg);
}

.comparison-table {
  background: var(--color-surface-alt);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-lg);
  overflow: hidden;
}

.comparison-table table {
  width: 100%;
  border-collapse: collapse;
}

.comparison-table th,
.comparison-table td {
  padding: var(--space-sm) var(--space-md);
  text-align: left;
}

.comparison-table th {
  background: var(--color-surface-raised);
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-text-muted);
  text-transform: uppercase;
}

.comparison-table tr:not(:last-child) td {
  border-bottom: 1px solid var(--color-border-light);
}

.comparison-table td:first-child {
  font-weight: 500;
}

/* Speed Zones */
.speed-zones-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-lg);
}

@media (max-width: 640px) {
  .speed-zones-container {
    grid-template-columns: 1fr;
  }
}

.speed-chart-wrapper {
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.speed-zones-table {
  background: var(--color-surface-alt);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-lg);
  overflow: hidden;
}

.speed-zones-table table {
  width: 100%;
  border-collapse: collapse;
}

.speed-zones-table th,
.speed-zones-table td {
  padding: var(--space-sm) var(--space-md);
  text-align: left;
}

.speed-zones-table th {
  background: var(--color-surface-raised);
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--color-text-muted);
  text-transform: uppercase;
}

.speed-zones-table tr:not(:last-child) td {
  border-bottom: 1px solid var(--color-border-light);
}

.zone-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: var(--radius-sm);
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
}

.zone-range {
  font-size: 0.8rem;
  color: var(--color-text-secondary);
}

.zone-percentage {
  font-weight: 600;
}

/* Acceleration Tab */
.accel-decel-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-lg);
}

@media (max-width: 640px) {
  .accel-decel-grid {
    grid-template-columns: 1fr;
  }
}

.accel-section,
.decel-section {
  background: var(--color-surface-alt);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-lg);
  padding: var(--space-md);
}

.accel-section h5,
.decel-section h5 {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  margin: 0 0 var(--space-md) 0;
  font-size: 0.9rem;
  font-weight: 600;
}

.accel-section h5 i { color: var(--color-success); }
.decel-section h5 i { color: var(--color-info); }

.band-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
}

.band-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.band-header {
  display: flex;
  justify-content: space-between;
  font-size: 0.8125rem;
}

.band-name {
  font-weight: 500;
}

.band-range {
  color: var(--color-text-muted);
}

.band-bar {
  height: 8px;
  background: var(--color-surface-raised);
  border-radius: var(--radius-full);
  overflow: hidden;
}

.band-fill {
  height: 100%;
  border-radius: var(--radius-full);
  transition: width 0.3s ease;
}

.band-count {
  font-size: 0.75rem;
  color: var(--color-text-secondary);
}

.peak-value {
  margin-top: var(--space-lg);
  padding-top: var(--space-md);
  border-top: 1px solid var(--color-border-light);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.peak-label {
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
}

.peak-number {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--color-primary-light);
}

/* History Tab */
.history-chart-container {
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
}

.metric-selector {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}

.metric-selector label {
  font-size: 0.875rem;
  color: var(--color-text-secondary);
}

.metric-selector select {
  padding: 6px 12px;
  background: var(--color-surface-alt);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-text);
  font-size: 0.875rem;
}

.history-chart-wrapper {
  height: 350px;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-2xl);
  color: var(--color-text-muted);
  text-align: center;
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: var(--space-md);
  opacity: 0.5;
}

.empty-state p {
  margin-bottom: var(--space-md);
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .modal-body {
    padding: var(--space-md);
  }

  .modal-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-md);
  }

  .header-actions {
    width: 100%;
    justify-content: flex-end;
  }

  .tabs-nav {
    overflow-x: auto;
    padding-bottom: var(--space-sm);
  }

  .tab-btn span {
    display: none;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
